import { SearchResultList } from '@/features/search/search-result-list/search-result-list'
import { useEffect, useRef } from 'react'
import { Plural, useLingui } from '@lingui/react/macro'
import { SearchSorting } from '@/features/search/search-sorting/search-sorting'
import { SearchNoResults } from '@/features/search/search-no-results/search-no-results'
import { useSortSearching } from '@/features/search/search-sorting/use-sort-searching'
import { useSortParams } from '@/features/search/use-sort-params/use-sort-params'
import { SearchInput } from '@vgw/multibrand-fe-search-input'
import { SearchFiltering } from '@/features/search/search-filtering/search-filtering'
import { useTracking } from '@vgw/multibrand-fe-tracking'
import { useSearchInput } from '@/features/search/use-search-input/use-search-input'
import { useFilteredGames } from '@/features/search/search-filtering/use-filtered-games'
import { useLocation, createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'
import { TagFilters } from '@/features/search/search-filtering/tag-filters'
import { SearchRootLayout } from '../../-search-root-layout'
import { useFilterParams } from '@/features/search/use-filter-params/use-filter-params'
import { useGamesToFilter } from '@/features/search/search-filtering/use-games-to-filter'
import { z } from 'zod'
import { assertCanPlayBeforeLoad } from '@/features/ctrls-and-caps/assert-can-play-before-load'
import { SearchResultsSkeleton } from '@/features/search/search-skeletons/search-results-skeleton'
import { SearchResultsCountLineSkeleton } from '@/features/search/search-skeletons/search-results-count-line-skeleton'

// eslint-disable-next-line react-refresh/only-export-components
export const sortValueSchema = z.union([
  z.literal('featured'),
  z.literal('game-title-az'),
  z.literal('game-title-za'),
  z.literal('release-date-az'),
  z.literal('release-date-za'),
])

// eslint-disable-next-line react-refresh/only-export-components
export const searchSchema = z.object({
  // search input query param
  query: z.string().optional(),
  // sort param
  sort: sortValueSchema.optional(),
  // filters param
  gameType: z.array(z.string()).optional(),
  provider: z.array(z.string()).optional(),
})

export const Route = createFileRoute('/_app/search')({
  component: Search,
  validateSearch: zodValidator(searchSchema),
  beforeLoad: async ({ context }) => {
    return assertCanPlayBeforeLoad(context.queryClient)
  },
})

function Search() {
  const query = Route.useSearch({
    select: ({ query }) => query,
  })

  const { t } = useLingui()
  const { sortValue } = useSortParams()
  const { filters, setFilters } = useFilterParams()

  const { gamesToFilter, isLoading: isGamesLoading } = useGamesToFilter()

  const { filteredResultCount, filteredGames } = useFilteredGames({
    selectedFilters: filters,
    gamesToFilter,
  })

  const sortedGames = useSortSearching({
    sort: sortValue,
    searchResults: filteredGames,
  })

  const { inputValue, onSearchInputChange, resetSearch } = useSearchInput()

  const { track } = useTracking()
  const { state } = useLocation()

  const from = useRef<string | undefined>((state as { from?: string })?.from)

  useEffect(() => {
    if (!from.current) {
      track('Game Search Accessed', {
        eventData: {
          'search entry point': 'deeplink',
        },
      })
    }
  }, [from, track])

  useEffect(() => {
    if (query && !isGamesLoading) {
      track('Game Search Result', {
        eventData: {
          'search query': query,
          'search game count': filteredResultCount,
        },
      })
    }
  }, [track, query, filteredResultCount, isGamesLoading])

  return (
    <SearchRootLayout>
      <main className="flex w-full flex-1 justify-center bg-search-surface">
        <div className="flex w-full max-w-[630px] flex-col gap-2 px-3 py-6 lg:px-6">
          <div className="flex flex-col gap-2">
            <SearchInput
              value={inputValue}
              onReset={resetSearch}
              onChange={onSearchInputChange}
              placeholder={t`Search by game name`}
              autoFocus // eslint-disable-line jsx-a11y/no-autofocus
            />
            <div className="flex flex-col gap-2">
              {query && !isGamesLoading ? (
                <div className="flex items-center justify-between">
                  <span
                    className="font-family-display text-lg leading-7 font-medium text-content"
                    data-testid="search-result-count"
                  >
                    <Plural
                      value={filteredResultCount}
                      one="# result"
                      other="# results"
                    />
                  </span>
                  <div className="flex gap-2">
                    <SearchSorting />
                    <SearchFiltering
                      selectedFilters={filters}
                      gamesToFilter={gamesToFilter}
                    />
                  </div>
                </div>
              ) : null}
              {query && isGamesLoading ? (
                <SearchResultsCountLineSkeleton />
              ) : null}
              <TagFilters filters={filters} setFilters={setFilters} />
            </div>
          </div>
          <div className="pb-12 md:pb-0">
            <SearchResultList results={sortedGames} />
            {query && isGamesLoading ? <SearchResultsSkeleton /> : null}
            {query && !isGamesLoading && !filteredResultCount ? (
              <SearchNoResults />
            ) : null}
          </div>
        </div>
      </main>
    </SearchRootLayout>
  )
}
